/**
 * The phase strip. The phase is derived from the calendar (Domain Model → Phase) by the one
 * engine behind `GET /admin/phase`, from the year's dates in Festival Admin → Year settings;
 * until a director sets them the strip lights nothing and says why. Nothing here guesses a date.
 */
import { useEffect, useState } from 'react';
import type { ShellPhase } from '@cider/ui';
import { api } from './api.js';

const UNSET: ShellPhase = {
  id: null,
  countdown: 'No festival dates yet — set them in Festival Admin → Year settings.',
};

interface PhaseAnswer {
  /** The engine's ids; the strip's fourth is `day`. */
  id: 'onboarding' | 'planning' | 'setup' | 'festival' | 'closeout' | null;
  countdown: string;
  festivalYear: number | null;
  /** The packet's count while chairs onboard (People Part 7): "41 of 64 chairs done". */
  onboarding: { chairs: number; done: number; expired: number; says: string } | null;
  tone: 'amber' | null;
  /** The public doors (Doors in Part 1): the strip names the open ones, from the same year settings the site reads. */
  doors?: {
    door: string;
    name: string;
    open: boolean;
    says: string;
    festivalYear: number | null;
  }[];
  /** The public site's origin, from SITE_ORIGIN (James, Oct 19): what a printed QR points at. */
  siteOrigin?: string;
}

let cached: PhaseAnswer | null = null;
const listeners = new Set<() => void>();
let asked = false;
function ask(): void {
  if (asked) return;
  asked = true;
  void api<PhaseAnswer>('GET', '/admin/phase')
    .then((p) => {
      cached = p;
      for (const l of listeners) l();
    })
    .catch(() => {
      asked = false;
    });
}
/** After a director sets the dates the strip asks again. */
export function refreshPhase(): void {
  asked = false;
  ask();
}

export function usePhase(): ShellPhase & {
  festivalYear: number | null;
  onboarding: PhaseAnswer['onboarding'];
  /** The year the doors write to: the open declaration door's, else the next one to open. */
  doorYear: number | null;
  /** The public site's origin — null until the API has answered; the app types none of its own. */
  siteOrigin: string | null;
} {
  const [, bump] = useState(0);
  useEffect(() => {
    const l = (): void => {
      bump((n) => n + 1);
    };
    listeners.add(l);
    ask();
    return () => {
      listeners.delete(l);
    };
  }, []);
  if (!cached)
    return { ...UNSET, festivalYear: null, onboarding: null, doorYear: null, siteOrigin: null };
  const openDoors = (cached.doors ?? []).filter((d) => d.open).map((d) => d.name);
  const declaration = (cached.doors ?? []).find((d) => d.door === 'declaration');
  return {
    id: cached.id === 'festival' ? 'day' : cached.id,
    countdown: [
      cached.countdown,
      cached.onboarding ? `Onboarding: ${cached.onboarding.says}` : null,
      openDoors.length ? `Doors open: ${openDoors.join(', ')}` : null,
    ]
      .filter(Boolean)
      .join(' · '),
    tone: cached.tone,
    festivalYear: cached.festivalYear,
    onboarding: cached.onboarding,
    doorYear: declaration?.festivalYear ?? null,
    siteOrigin: cached.siteOrigin ?? null,
  };
}
